import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  View,
  Text,
  Image,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { CommonButton } from "../../common/CommonButton";
import { useLiveStream } from "../../context/LiveStreamContext";
import StopStreamingPopup from "./StopStreamingPopup";
import { AppImages } from "../../utility/AppImages";
import { AppFontStyle } from "../../styles/AppFontStyle";
import {
  EVENT_STATUS,
  STREAM_STATUS,
} from "../../utility/LiveStreamSocket/LiveStreamConfig";
import {
  gpLiveAndUpdateBroadcastId,
  updateStreamDetailsAfterStreamComplete,
  updateStreamStatusOnStartAndStopRecording,
  UpdateYoutubeEmbeddingSettingsForUser,
} from "../../services/stream";
import { updateEventAutoLive } from "../../services/eventsAPI";
import { useWsProvider } from "../../context/NativeApplicationContext";
import { AppColors } from "../../utility/AppColors";

const COLORS = {
  green: "#4ADE80",
  purple: "#6D08C3",
  purpleThumb: "#6D08C3",
  yellow: "#FDE047",
  darkGreen: "#14532D",
  blue: "#284CB8",
  liveRed: "#DC2626",
  redBorder: "#C3081B",
  obsBg: "#14532D",
  obsText: "#16A34A",
  liveBg: "#7F1D1D",
  liveText: "#FFF",
};

const getStatusDisplayText = (state?: string): string => {
  switch (state) {
    case "service-starting":
      return "Service Starting";
    case "idle":
      return "Ready";
    case "streaming-starting":
      return "Starting Stream";
    case "streaming":
      return "Streaming Live";
    case "network-disconnected":
      return "Network Disconnected";
    case "network-reconnecting":
      return "Reconnecting Network";
    case "restarting-streaming":
      return "Restarting Stream";
    case "service-error":
      return "Service Error";
    case "service-restarting":
      return "Restarting Service";
    case "streaming-error":
      return "Streaming Error";
    default:
      return state || "Ready";
  }
};

const getStatusColor = (state?: string): string => {
  switch (state) {
    case "idle":
      return "#22C55E"; // Green - ready
    case "streaming":
      return "#22C55E"; // Green - actively streaming
    case "service-starting":
    case "streaming-starting":
    case "network-reconnecting":
    case "restarting-streaming":
    case "service-restarting":
      return "#F59E0B"; // Yellow - in progress
    case "network-disconnected":
    case "service-error":
    case "streaming-error":
      return "#EF4444"; // Red - error state
    default:
      return "#6B7280"; // Gray - unknown/default
  }
};

const StreamControlPanel = ({
  startStream,
  toggleStream,
  startRecording,
  toggleRecording,
  accessToken,
  updateModalState,
  isStreamStarting,
  loading,
}: {
  startStream: boolean;
  toggleStream: any;
  startRecording: boolean;
  toggleRecording: () => Promise<void>;
  accessToken: string;
  isStreamStarting: boolean;
  loading: boolean;
  updateModalState: any;
}) => {
  const {
    eventStartTime,
    streamStatus,
    userRole,
    currentEventId,
    organizerData,
    eventStatus,
    setEventEndTime,
    setOrganiserStreamStatus,
    setChallengerStreamStatus,
    setOrganizerData,
  } = useLiveStream();
  const {
    nativeAppConnectionState,
    nativeAppStatus,
    isNativeAppConnected,
    canStartStreaming,
    canStopStreaming,
    canStartRecording,
    canStopRecording,
    shouldShowReconnect,
    clearError,
    recordingFile,
    streamStarted: currentStreamStarted,
    smartReconnect,
    connectWithDeeplink,
  } = useWsProvider();
  const [stopStreaming, setStopStreaming] = useState(false);
  const [isStopping, setIsStopping] = useState(false);
  const [autoGoLive, setAutoGoLive] = useState(false);
  const [isAutoLiveLoading, setIsAutoLiveLoading] = useState(false);
  const [isEventActive, setIsEventActive] = useState(false);
  const [timeLeft, setTimeLeft] = useState({ minutes: 0, seconds: 0 });
  const [eventHasStarted, setEventHasStarted] = useState(
    eventStartTime ? new Date(eventStartTime) <= new Date() : false
  );
  const [hasTriggeredStream, setHasTriggeredStream] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isAutoTriggeringRef = useRef<boolean>(false);
  const [liveCountdown, setLiveCountdown] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [showLiveCountdown, setShowLiveCountdown] = useState(false);
  const liveCountdownRef = useRef<NodeJS.Timeout | null>(null);

  const { streamingPlatforms } = organizerData?.eventDetails || {};

  useEffect(() => {
    if (organizerData?.eventDetails?.autoLive?.[userRole] !== undefined) {
      setAutoGoLive(organizerData.eventDetails.autoLive[userRole]);
    }
  }, [organizerData, userRole]);


  // useEffect(() => {
  //   if (
  //     eventStatus === EVENT_STATUS.LIVE &&
  //     !effectiveConnectionStatus &&
  //     !hasCalledPauseApiRef.current
  //   ) {
  //     console.log("Calling updateAudienceOnStreamPaused due to disconnection");
  //     hasCalledPauseApiRef.current = true;
  //     updateAudienceOnStreamPaused(accessToken, currentEventId, "paused")
  //       .then((response) => {
  //         console.log("updateAudienceOnStreamPaused response:", response);
  //       })
  //       .catch((error) => {
  //         console.error("Error in updateAudienceOnStreamPaused:", error);
  //         hasCalledPauseApiRef.current = false; // Allow retry on failure
  //       });
  //   }
  //   // Reset the flag when event is not live or connection is restored
  //   if (eventStatus !== EVENT_STATUS.LIVE || effectiveConnectionStatus) {
  //     hasCalledPauseApiRef.current = false;
  //   }
  // }, [eventStatus, effectiveConnectionStatus, accessToken, currentEventId]);

  const handleStopStream = useCallback(async () => {
    console.log("handleStopStream: starting");
    setIsStopping(true);
    try {
      if (startStream) {
        console.log("handleStopStream: calling toggleStream");
        const success = await toggleStream();
        console.log("handleStopStream: toggleStream returned", success);
        if (!success && currentStreamStarted) {
          console.error("Failed to stop stream via toggleStream");
          setStopStreaming(false);
          setIsStopping(false);
          return;
        }
      }
      console.log("handleStopStream: calling API");
      const response = await updateStreamStatusOnStartAndStopRecording(
        accessToken,
        currentEventId,
        STREAM_STATUS.COMPLETED
      );
      console.log("handleStopStream: API response", response);
      if (response.statusCode === 200 && response.body) {
        if (userRole === "organiser") {
          setOrganiserStreamStatus(STREAM_STATUS.COMPLETED);
        } else if (userRole === "challenger") {
          setChallengerStreamStatus(STREAM_STATUS.COMPLETED);
        }
        setEventEndTime(response.body.eventEndTime || new Date().toISOString());
        setEventHasStarted(true);
        clearError();
        setStopStreaming(false);
        updateModalState("isEventCompletedModalVisible", true);
        console.log("handleStopStream: success, closing modal");
        const platforms = streamingPlatforms?.[userRole] || [];
        if (platforms.includes("Twitch")) {
          updateStreamDetailsAfterStreamComplete(currentEventId, accessToken);
        }
      } else {
        console.log(
          "handleStopStream: API failed, statusCode:",
          response.statusCode
        );
      }
    } catch (error) {
      console.error("Error stopping stream:", error);
      setStopStreaming(false);
      setIsStopping(false);
    } finally {
      setIsStopping(false);
      console.log("handleStopStream: finally, isStopping set to false");
    }
  }, [
    startStream,
    toggleStream,
    accessToken,
    currentEventId,
    userRole,
    setEventEndTime,
    setOrganiserStreamStatus,
    setChallengerStreamStatus,
    clearError,
    updateModalState,
    currentStreamStarted,
    streamingPlatforms,
  ]);

  const handleStartStream = useCallback(async () => {
    if (!canStartStreaming) {
      console.warn("Cannot start stream: Invalid state for streaming operation");
      return;
    }
    try {
      await toggleStream();
      const response = await updateStreamStatusOnStartAndStopRecording(
        accessToken,
        currentEventId,
        STREAM_STATUS.LIVE
      );
      if (response.statusCode === 200 && response.body) {
        if (userRole === "organiser") {
          setOrganiserStreamStatus(STREAM_STATUS.LIVE);
        } else if (userRole === "challenger") {
          setChallengerStreamStatus(STREAM_STATUS.LIVE);
        }

        // Immediately update organizerData to trigger preview display
        // This fixes the bug where preview doesn't show because eventDetails.status
        // wasn't being updated locally until WebSocket message arrived
        setOrganizerData((prev: any) => ({
          ...prev,
          eventDetails: {
            ...prev.eventDetails,
            status: EVENT_STATUS.LIVE,
            streamStatus: {
              ...prev.eventDetails?.streamStatus,
              [userRole]: STREAM_STATUS.LIVE,
            },
          },
        }));

        setEventHasStarted(true);
        clearError();
      }
      const platforms = streamingPlatforms?.[userRole] || [];
      if (platforms.includes("Twitch")) {
        const twitchResponse = await gpLiveAndUpdateBroadcastId(currentEventId, accessToken);
        console.log("gpLiveAndUpdateBroadcastId response:", twitchResponse);

        // Update organizerData with the liveChatUrl from the response
        if (twitchResponse?.statusCode === 200 && twitchResponse?.body) {
          const responseBody = typeof twitchResponse.body === "string"
            ? JSON.parse(twitchResponse.body)
            : twitchResponse.body;

          if (responseBody?.liveChatUrl) {
            setOrganizerData((prev: any) => {
              const streamKey = userRole === "organiser" ? "organizerTwitchStream" : "challengerTwitchStream";
              return {
                ...prev,
                streamDetails: prev.streamDetails?.map((detail: any, index: number) => {
                  if (index === 0) {
                    return {
                      ...detail,
                      [streamKey]: {
                        ...detail[streamKey],
                        liveChatUrl: responseBody.liveChatUrl,
                        liveStreamURL: responseBody.liveStreamURL,
                      },
                    };
                  }
                  return detail;
                }) || [{
                  [streamKey]: {
                    liveChatUrl: responseBody.liveChatUrl,
                    liveStreamURL: responseBody.liveStreamURL,
                  },
                }],
              };
            });
          }
        }
      }
      if (platforms.includes("YouTube")) {
        await UpdateYoutubeEmbeddingSettingsForUser(
          currentEventId,
          accessToken
        );
      }
    } catch (error) {
      console.error("Error updating stream status:", error);
    }
  }, [
    canStartStreaming,
    toggleStream,
    accessToken,
    currentEventId,
    userRole,
    streamingPlatforms,
    setOrganiserStreamStatus,
    setChallengerStreamStatus,
    setOrganizerData,
    clearError,
  ]);

  const handleStreamToggle = useCallback(async () => {
    if (!isNativeAppConnected) {
      console.warn("Cannot toggle stream: Native app not connected");
      return;
    }

    if (!canStartStreaming && !canStopStreaming) {
      console.warn("Cannot toggle stream: Invalid state for streaming operation");
      return;
    }

    if (startStream) {
      setStopStreaming(true);
    } else {
      await handleStartStream();
    }
  }, [isNativeAppConnected, canStartStreaming, canStopStreaming, startStream, handleStartStream]);

  const handleRecordingToggle = useCallback(async () => {
    if (!isNativeAppConnected) {
      console.warn("Cannot toggle recording: Native app not connected");
      return;
    }

    if (!canStartRecording && !canStopRecording) {
      console.warn("Cannot toggle recording: Invalid state for recording operation");
      return;
    }

    try {
      await toggleRecording();
      const status = startRecording
        ? STREAM_STATUS.COMPLETED
        : STREAM_STATUS.LIVE;
      await updateStreamStatusOnStartAndStopRecording(
        accessToken,
        currentEventId,
        status
      );
      clearError();
    } catch (error) {
      console.error("Error toggling recording:", error);
    }
  }, [
    isNativeAppConnected,
    canStartRecording,
    canStopRecording,
    toggleRecording,
    startRecording,
    accessToken,
    currentEventId,
    clearError,
  ]);

  const handleAutoGoLiveToggle = useCallback(async () => {
    if (!isNativeAppConnected) {
      console.warn("Cannot toggle Auto-Go-Live: Native app not connected");
      return;
    }
    const newAutoGoLive = !autoGoLive;
    setIsAutoLiveLoading(true);
    try {
      const response = await updateEventAutoLive(
        accessToken,
        currentEventId,
        newAutoGoLive
      );
      if (response.statusCode === 200) {
        setAutoGoLive(newAutoGoLive);
        clearError();
      }
    } catch (error) {
      console.error("Error updating autoLive:", error);
    } finally {
      setIsAutoLiveLoading(false);
    }
  }, [
    isNativeAppConnected,
    autoGoLive,
    accessToken,
    currentEventId,
    clearError,
  ]);

  const updateTimer = useCallback(() => {
    if (!eventStartTime) return;
    const startTime = new Date(eventStartTime);
    const fiveMinBefore = new Date(startTime.getTime() - 5 * 60 * 1000);
    const now = new Date();
    const diffMs = Math.max(0, fiveMinBefore.getTime() - now.getTime());
    const diffMin = Math.floor(diffMs / (1000 * 60));
    const diffSec = Math.floor((diffMs % (1000 * 60)) / 1000);

    setTimeLeft((prev) => {
      if (prev.minutes !== diffMin || prev.seconds !== diffSec) {
        return { minutes: diffMin, seconds: diffSec };
      }
      return prev;
    });
    setIsEventActive(diffMs <= 0);

    if (
      diffMin === 0 &&
      diffSec === 0 &&
      autoGoLive &&
      !startStream &&
      streamStatus?.[userRole] !== STREAM_STATUS.LIVE &&
      !hasTriggeredStream &&
      !isAutoTriggeringRef.current &&
      canStartStreaming
    ) {
      isAutoTriggeringRef.current = true;
      setHasTriggeredStream(true);
      handleStreamToggle().finally(() => {
        isAutoTriggeringRef.current = false;
      });
    }
  }, [
    eventStartTime,
    autoGoLive,
    handleStreamToggle,
    startStream,
    streamStatus,
    userRole,
    hasTriggeredStream,
    canStartStreaming,
  ]);

  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (
      !eventStartTime ||
      eventHasStarted ||
      startStream ||
      streamStatus?.[userRole] === STREAM_STATUS.LIVE
    ) {
      return;
    }

    updateTimer();
    timerRef.current = setInterval(updateTimer, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    eventStartTime,
    eventHasStarted,
    startStream,
    streamStatus,
    userRole,
    updateTimer,
  ]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // Live countdown timer - shows when streaming before event start time
  useEffect(() => {
    if (liveCountdownRef.current) {
      clearInterval(liveCountdownRef.current);
      liveCountdownRef.current = null;
    }

    if (!startStream || !eventStartTime) {
      setShowLiveCountdown(false);
      return;
    }

    const updateLiveCountdown = () => {
      const now = new Date();
      const eventTime = new Date(eventStartTime);
      const diffMs = Math.max(0, eventTime.getTime() - now.getTime());

      if (diffMs > 0) {
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
        setLiveCountdown({ hours, minutes, seconds });
        setShowLiveCountdown(true);
      } else {
        setShowLiveCountdown(false);
        setEventHasStarted(true);
      }
    };

    updateLiveCountdown();
    liveCountdownRef.current = setInterval(updateLiveCountdown, 1000);

    return () => {
      if (liveCountdownRef.current) {
        clearInterval(liveCountdownRef.current);
        liveCountdownRef.current = null;
      }
    };
  }, [startStream, eventStartTime]);

  useEffect(() => {
    if (eventStatus === EVENT_STATUS.COMPLETED && userRole === "challenger") {
      handleStopStream();
    }
  }, [eventStatus, userRole, handleStopStream]);

  useEffect(() => {
    const platforms = streamingPlatforms?.[userRole] || [];
    if (
      (streamStatus?.[userRole] === STREAM_STATUS.COMPLETED ||
        streamStatus?.[userRole] === STREAM_STATUS.LIVE) &&
      platforms.includes("Twitch")
    ) {
      updateStreamDetailsAfterStreamComplete(currentEventId, accessToken);
    }
  }, [streamStatus, userRole, streamingPlatforms, currentEventId, accessToken]);

  return (
    <View
      style={{
        backgroundColor: AppColors.grayColor_282828,
        padding: 12,
        borderRadius: 8,
        width: "25%",
      }}
    >
      <Text style={{ color: "#FFF", fontSize: 16, marginBottom: 6 }}>
        Controls
      </Text>
      {startStream && showLiveCountdown && (
        <View
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: COLORS.liveBg,
            paddingHorizontal: 10,
            paddingVertical: 6,
            borderRadius: 12,
            flexDirection: "row",
            alignItems: "center",
            borderColor: COLORS.liveRed,
            borderWidth: 1,
            minWidth: 180,
          }}
        >
          <Image
            source={AppImages.MatchTime}
            style={{ height: 14, width: 14, marginRight: 6 }}
            resizeMode="contain"
          />
          <Text style={{ color: COLORS.liveText, fontSize: 11, fontWeight: "600" }}>
            Stream goes live in: {liveCountdown.hours > 0 ? `${liveCountdown.hours}:` : ""}{liveCountdown.minutes.toString().padStart(2, '0')}:{liveCountdown.seconds.toString().padStart(2, '0')}
          </Text>
        </View>
      )}
      {isEventActive && startStream && !showLiveCountdown && (
        <View
          style={{
            position: "absolute",
            top: 8,
            right: 8,
            backgroundColor: COLORS.liveBg,
            paddingHorizontal: 8,
            paddingVertical: 4,
            borderRadius: 12,
            flexDirection: "row",
            alignItems: "center",
            borderColor: COLORS.liveRed,
            borderWidth: 1,
          }}
        >
          <Image
            source={AppImages.Live_Stream}
            style={{ height: 12, width: 12, marginRight: 6 }}
            resizeMode="contain"
          />
          <Text style={{ color: COLORS.liveText, fontSize: 12 }}>LIVE</Text>
        </View>
      )}
      {autoGoLive &&
        !isEventActive &&
        timeLeft.minutes !== null &&
        timeLeft.minutes >= 0 &&
        timeLeft.minutes < 60 && (
          <View
            style={{
              position: "absolute",
              top: 14,
              right: 12,
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Image
              source={AppImages.MatchTime}
              style={{ height: 18, width: 18, marginRight: 6 }}
              resizeMode="contain"
            />
            <Text
              style={{ color: COLORS.yellow, fontSize: 12 }}
              accessible
              accessibilityLabel={`Event goes live in ${timeLeft.minutes} minutes and ${timeLeft.seconds} seconds`}
            >
              Event goes live in: {timeLeft.minutes}:
              {timeLeft.seconds < 10
                ? `0${timeLeft.seconds}`
                : timeLeft.seconds}
            </Text>
          </View>
        )}
      {isEventActive && autoGoLive && !startStream && (
        <View
          style={{
            position: "absolute",
            top: 14,
            right: 12,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Image
            source={AppImages.MatchTime}
            style={{ height: 18, width: 18, marginRight: 6 }}
            resizeMode="contain"
          />
          <Text style={{ color: COLORS.green, fontSize: 12, marginBottom: 8 }}>
            Stream is starting...
          </Text>
        </View>
      )}
      {/* Detailed Native App Status Box */}
      {isNativeAppConnected && (
        <View style={{
          backgroundColor: "rgba(40, 40, 40, 0.6)",
          padding: 6,
          borderRadius: 4,
          marginBottom: 8,
          borderWidth: 1,
          borderColor: "rgba(255, 255, 255, 0.1)",
        }}>
          {/* Status Row - Compact Layout */}
          <View style={{ flexDirection: "row", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Text style={{ color: "#FFF", fontSize: 9, fontWeight: "600", marginRight: 3 }}>
                Stream:
              </Text>
              <Text style={{
                color: nativeAppStatus?.isStreaming ? "#22C55E" : "#6B7280",
                fontSize: 9
              }}>
                {nativeAppStatus?.isStreaming ? "🔴 Live" : "⚫ Off"}
              </Text>
            </View>

            <View style={{ flexDirection: "row", alignItems: "center" }}>
              <Text style={{ color: "#FFF", fontSize: 9, fontWeight: "600", marginRight: 3 }}>
                Record:
              </Text>
              <Text style={{
                color: nativeAppStatus?.isRecording ? "#EF4444" : "#6B7280",
                fontSize: 9
              }}>
                {nativeAppStatus?.isRecording ? "🟢 On" : "⚫ Off"}
              </Text>
            </View>
          </View>

          {/* Service State - Compact */}
          <View style={{ flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
            <Text style={{
              color: getStatusColor(nativeAppStatus?.state),
              fontSize: 9,
              fontWeight: "500"
            }}>
              {getStatusDisplayText(nativeAppStatus?.state)}
            </Text>
          </View>

          {/* Recording File Path - Only show filename */}
          {recordingFile && (
            <View style={{ marginTop: 2 }}>
              <Text style={{ color: "#9CA3AF", fontSize: 8, textAlign: "center" }} numberOfLines={1}>
                📁 {recordingFile.split('/').pop() || recordingFile}
              </Text>
            </View>
          )}
        </View>
      )}
      <CommonButton
        title={
          startStream
            ? "Stop Stream"
            : isStreamStarting
              ? "Starting..."
              : "Start Stream"
        }
        onTap={handleStreamToggle}
        disabled={
          (!canStartStreaming && !canStopStreaming) ||
          (!startStream && eventStartTime && new Date() < new Date(new Date(eventStartTime).getTime() - 5 * 60 * 1000) && eventStatus !== EVENT_STATUS.COMPLETED) ||
          eventStatus === EVENT_STATUS.COMPLETED ||
          isStreamStarting ||
          loading
        }
        buttonStyle={{
          backgroundColor: startStream
            ? COLORS.blue
            : AppColors.buttonRed_DC2626,
          borderWidth: 2,
          borderColor: startStream
            ? COLORS.blue
            : AppColors.buttonRedDark_991B1B,
          paddingVertical: 8,
          paddingHorizontal: 12,
          borderRadius: 8,
          marginBottom: 6,
          marginTop: 8,
          shadowColor: "transparent",
          shadowOpacity: 0,
          elevation: 0,
        }}
        disabledCustomStyle={{
          backgroundColor: startStream ? COLORS.blue : AppColors.buttonRedDark_991B1B,
          opacity: 0.7,
        }}
        image={true}
        imageUrl={startStream ? AppImages.Stop_Stream : AppImages.Start_stream}
        imageStyle={{
          width: 20,
          height: 20,
          marginRight: 8,
          resizeMode: "contain",
        }}
        imagePosition="pre"
      />
      <CommonButton
        title={startRecording ? "Stop Recording" : "Start Recording"}
        onTap={handleRecordingToggle}
        disabled={!canStartRecording && !canStopRecording || isStreamStarting || loading}
        buttonStyle={{
          backgroundColor: "#000",
          borderWidth: 2,
          borderColor: startRecording ? COLORS.redBorder : "#FFF",
          paddingVertical: 8,
          paddingHorizontal: 12,
          borderRadius: 8,
          marginBottom: 6,
          shadowColor: "transparent",
          shadowOpacity: 0,
          elevation: 0,
          marginTop: 2,
        }}
        disabledCustomStyle={{
          backgroundColor: "#000",
          opacity: 0.7,
        }}
        image={true}
        imageUrl={
          startRecording ? AppImages.Stop_Recording : AppImages.Record_Stream
        }
        imageStyle={{
          width: 20,
          height: 20,
          marginRight: 8,
          resizeMode: "contain",
        }}
        imagePosition="pre"
      />
      {!startStream && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: 8,
            marginBottom: 6,
            marginTop: 8,
          }}
        >
          <Text
            style={{ color: COLORS.liveText, ...AppFontStyle.BOLD_ANYBODY_16 }}
          >
            Auto-Go-Live
          </Text>
          <TouchableOpacity
            style={{ height: 27, aspectRatio: 2 }}
            onPress={handleAutoGoLiveToggle}
            disabled={isAutoLiveLoading || !isNativeAppConnected}
          >
            {isAutoLiveLoading ? (
              <View style={{ justifyContent: "center", alignItems: "center" }}>
                <ActivityIndicator size="small" color={COLORS.yellow} />
              </View>
            ) : (
              <Image
                source={autoGoLive ? AppImages.Toggle_On : AppImages.Toggle_Off}
                style={{ height: 27, width: "100%", resizeMode: "contain" }}
              />
            )}
          </TouchableOpacity>
        </View>
      )}
      {!startStream && (
        <Text style={{ color: COLORS.green, fontSize: 10, marginBottom: 6 }}>
          Will activate 5 mins before event
        </Text>
      )}
      {startStream && (
        <View
          style={{
            backgroundColor: COLORS.obsBg,
            padding: 8,
            borderRadius: 8,
            borderWidth: 2,
            borderColor: COLORS.obsText,
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <Image
            source={AppImages.Obs_Icon}
            style={{ height: 24, width: 24, marginRight: 6 }}
            resizeMode="contain"
          />
          <View style={{ flexDirection: "column" }}>
            <Text style={{ color: COLORS.obsText }}>KO Broadcaster</Text>
            <Text style={{ color: COLORS.obsText }}>
              {nativeAppConnectionState === 'native-app-connected'
                ? `Connected - ${nativeAppStatus?.state || 'idle'}`
                : nativeAppConnectionState === 'native-app-connecting'
                  ? "Connecting..."
                  : "Disconnected"}
            </Text>
          </View>
        </View>
      )}
      {shouldShowReconnect && (
        <CommonButton
          title="🔄 Smart Reconnect"
          onTap={smartReconnect}
          buttonStyle={{
            backgroundColor: COLORS.redBorder,
            borderWidth: 2,
            borderColor: COLORS.redBorder,
            paddingVertical: 10,
            paddingHorizontal: 14,
            borderRadius: 8,
            marginTop: 8,
            shadowColor: "transparent",
            shadowOpacity: 0,
            elevation: 0,
          }}
          textStyle={{ color: "#FFF" }}
        />
      )}
      {!isNativeAppConnected && (
        <CommonButton
          title="Connect to KO Broadcaster"
          onTap={() => {
            // Use deeplink to open KO Broadcaster if not running
            connectWithDeeplink().catch(() => {
              // If it fails, show error message
            });
          }}
          buttonStyle={{
            backgroundColor: COLORS.yellow,
            borderWidth: 2,
            borderColor: COLORS.yellow,
            paddingVertical: 10,
            paddingHorizontal: 14,
            borderRadius: 8,
            marginTop: 8,
            shadowColor: "transparent",
            shadowOpacity: 0,
            elevation: 0,
          }}
          textStyle={{ color: "#000" }}
        />
      )}
      <StopStreamingPopup
        stopStreaming={stopStreaming}
        setStopStreaming={setStopStreaming}
        onConfirm={handleStopStream}
        disabled={isStopping}
      />
    </View>
  );
};

export default StreamControlPanel;
